import { Column, ColumnApi, ColumnGroup, GridApi, RowNode } from 'ag-grid';
import { CustomDate } from './format-date-helper';
import {
  IDataExportRow,
  IExportTemplate,
} from '../interfaces/data-export.interface';
import { ExportRowsEnum } from '../enums/data-export.enum';

declare const XLSX: any;

export class DataExportHelper {
  private static readonly grandTotalLabel = 'Grand Total';

  private static readonly isoDatePattern = /^\d{4}-\d{2}-\d{2}(T|$)/;
  private static readonly formulaPrefixPattern = /^[=+\-@]/;
  private static readonly invalidSheetNamePattern = /[\\\/\?\*\[\]:]/g;
  private static readonly maxSheetNameLength = 31;

  static write(
    rows: IDataExportRow[],
    fileName: string,
    sheetName: string,
    ext: string,
  ): void {
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, this.sanitizeSheetName(sheetName));
    XLSX.writeFile(wb, fileName + '.' + ext);
  }

  static rowsFromGrid(
    gridApi: GridApi,
    columnApi: ColumnApi,
    rowsMode: ExportRowsEnum,
    excluded: string[],
  ): IDataExportRow[] {
    const nodes = this.nodes(gridApi, rowsMode);
    const columns = columnApi
      .getAllDisplayedColumns()
      .filter(
        (c: Column) =>
          excluded.indexOf(c.getColId()) === -1 &&
          !c.getColDef().checkboxSelection,
      );
    const headers = this.headers(columns);
    return nodes.map((node: RowNode) => {
      const row: IDataExportRow = {};
      columns.forEach((col: Column, i: number) => {
        const val = this.normalize(gridApi.getValue(col, node));
        row[headers[i]] =
          node.footer && i === 0 && val === '' ? this.grandTotalLabel : val;
      });
      return row;
    });
  }

  static rowsFromTemplate(
    data: any[],
    template: IExportTemplate,
  ): IDataExportRow[] {
    return data.map((item: any) => {
      const row: IDataExportRow = {};
      Object.keys(template).forEach((header: string) => {
        const accessor = template[header];
        const value =
          typeof accessor === 'function'
            ? accessor(item)
            : this.readPath(item, accessor);
        row[header] = this.normalize(value);
      });
      return row;
    });
  }

  static selectedCount(gridApi: GridApi): number {
    return this.nodes(gridApi, ExportRowsEnum.Selected).length;
  }

  static toYesNo(value: boolean): string {
    return value ? 'Yes' : 'No';
  }

  static toDate(value: string): string {
    const formatted = CustomDate.formatJs(value, 'MM-dd-yyyy');
    return formatted ? formatted.replace(/-/g, '/') : '';
  }

  static readPath(item: any, path: string): any {
    return path
      .split('.')
      .reduce((cur: any, k: string) => (cur == null ? cur : cur[k]), item);
  }

  static normalize(value: any): string | number {
    if (!value && value !== 0) {
      return '';
    }
    if (typeof value === 'boolean') {
      return this.toYesNo(value);
    }
    if (value instanceof Date) {
      return this.toDate(value.toISOString());
    }
    if (typeof value === 'string' && this.isoDatePattern.test(value)) {
      return this.toDate(value);
    }
    if (typeof value === 'object') {
      return '';
    }
    return typeof value === 'string' ? this.sanitizeText(value) : value;
  }

  private static nodes(gridApi: GridApi, rowsMode: ExportRowsEnum): RowNode[] {
    const nodes: RowNode[] = [];
    if (rowsMode === ExportRowsEnum.Selected) {
      gridApi.forEachNodeAfterFilter((n: RowNode) => {
        if (n.isSelected()) {
          nodes.push(n);
        }
      });
      return nodes;
    }
    const withFooters = rowsMode === ExportRowsEnum.AllWithFooters;
    gridApi.forEachNodeAfterFilterAndSort((n: RowNode) => {
      if (!(n.footer && !withFooters)) {
        nodes.push(n);
      }
    });
    if (withFooters) {
      const pinned = gridApi.getPinnedBottomRowCount();
      for (let i = 0; i < pinned; i += 1) {
        const p = gridApi.getPinnedBottomRow(i);
        if (p) {
          nodes.push(p);
        }
      }
    }
    return nodes;
  }

  private static headers(columns: Column[]): string[] {
    const used: { [h: string]: boolean } = {};
    return columns.map((col: Column) => {
      const base = this.header(col);
      let name = base;
      let n = 2;
      while (used[name]) {
        name = base + ' ' + n;
        n += 1;
      }
      used[name] = true;
      return name;
    });
  }

  private static header(col: Column): string {
    const own = this.clean(col.getColDef().headerName, col.getColId());
    const parent: ColumnGroup = col.getParent();
    const pDef = parent && parent.getColGroupDef();
    const parentName =
      pDef && pDef.headerName ? this.clean(pDef.headerName, '') : '';
    return parentName ? parentName + ' ' + own : own;
  }

  private static clean(name: string, fallback: string): string {
    return (name || fallback || '').replace(/<[^>]*>/g, '').trim() || fallback;
  }

  private static sanitizeText(value: string): string {
    return this.formulaPrefixPattern.test(value) ? '\'' + value : value;
  }

  private static sanitizeSheetName(sheetName: string): string {
    const cleaned = (sheetName || '')
      .replace(this.invalidSheetNamePattern, '')
      .trim();
    return cleaned.substring(0, this.maxSheetNameLength) || 'Export';
  }
}



// WEBPACK FOOTER //
// ./src/app/core/helpers/data-export.helper.ts